import { useTranslation } from 'react-i18next';
import { FormControl, FormField, FormItem, FormLabel } from 'components/ui/form';
import { useFormContext } from 'react-hook-form';
import { Button } from 'components/ui/button';
import type { ChatbotWidget } from './types';
import { useEffect } from 'react';
import { ColorSuggestionForm, WidgetColorPalette } from './color-suggestion-form';
import { ColorSwatch } from './color-swatch';
import { cn } from 'lib/utils';

const sectionTitleClass =
  'text-lg font-semibold leading-[1.5] text-[#3d3d3d] dark:text-foreground';

const colorLabelClass =
  'text-base font-semibold leading-[1.2] tracking-normal whitespace-nowrap text-[#3d3d3d] dark:text-[#e7e7e7]';

const colorRowClass = 'flex flex-wrap items-start gap-x-12 gap-y-5 lg:gap-x-20 xl:gap-x-[100px]';

const colorFieldClass = 'flex w-[120px] flex-col items-start gap-2 space-y-0';

type ColorFieldProps = {
  name:
  | 'designSettings.headerStartColor'
  | 'designSettings.headerEndColor'
  | 'designSettings.titleColor'
  | 'designSettings.chatBackgroundColor'
  | 'designSettings.chatFontColor'
  | 'designSettings.sendButtonBackgroundColor'
  | 'designSettings.sendButtonFontColor'
  | 'designSettings.borderColor'
  | 'designSettings.chat-toggle-button--background-color'
  | 'designSettings.chat-toggle-button--color';
  label: string;
  fallback: string;
};

function ColorField({ name, label, fallback }: ColorFieldProps) {
  const form = useFormContext<ChatbotWidget>();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={colorFieldClass}>
          <FormLabel className={colorLabelClass}>{label}</FormLabel>
          <FormControl>
            <ColorSwatch
              value={field.value || fallback}
              onChange={(hex) => field.onChange(hex)}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

export const AgentIntegrationChatbotDesign = ({
  canEdit = true,
}: {
  canEdit?: boolean;
}) => {
  const { t } = useTranslation();
  const form = useFormContext<ChatbotWidget>();

  const handleSuggestions = ({
    values,
    suggestion,
  }: {
    values: { url: string; brand_color: string };
    suggestion: WidgetColorPalette;
  }) => {
    form.setValue(
      'designSettings.headerStartColor',
      suggestion['chat-header--background-start-color']
    );
    form.setValue('designSettings.headerEndColor', suggestion['chat-header--background-end-color']);
    form.setValue('designSettings.titleColor', suggestion['chat-header--title-color']);
    form.setValue('designSettings.borderColor', suggestion['chat--border-color']);
    form.setValue(
      'designSettings.chatBackgroundColor',
      suggestion['chat-window--background-color']
    );
    form.setValue('designSettings.chatFontColor', suggestion['chat-window--font-color']);
    form.setValue(
      'designSettings.sendButtonBackgroundColor',
      suggestion['send-button--background-color']
    );
    form.setValue('designSettings.sendButtonFontColor', suggestion['send-button--font-color']);
    form.setValue(
      'designSettings.chat-toggle-button--background-color',
      suggestion['chat-toggle-button--background-color']
    );
    form.setValue(
      'designSettings.chat-toggle-button--color',
      suggestion['chat-toggle-button--color']
    );
    form.setValue('site_url', values.url);
    form.setValue('brand_color', values.brand_color, { shouldDirty: true });
  };

  useEffect(() => {
    const fields = [
      'designSettings.headerStartColor',
      'designSettings.headerEndColor',
      'designSettings.titleColor',
      'designSettings.chatBackgroundColor',
      'designSettings.chatFontColor',
      'designSettings.sendButtonBackgroundColor',
      'designSettings.sendButtonFontColor',
      'designSettings.borderColor',
    ] as const;

    fields.forEach((field) => {
      form.register(field);
    });
  }, [form]);

  const { isValid } = form.formState;

  return (
    <div className="flex w-full flex-col gap-8 pb-4">
      {/* <ColorSuggestionForm
        values={{
          url: form.getValues('site_url') || '',
          brand_color: form.getValues('brand_color') || '',
        }}
        onSuggestion={handleSuggestions}
      /> */}

      <div className="flex w-full flex-col gap-7">
        {/* Chat Header */}
        <div className="flex w-full flex-col gap-5 border-b border-[#e7e7e7] pb-6 dark:border-border">
          <p className={sectionTitleClass}>{t('CHAT_HEADER') || 'Chat Header'}</p>
          <div className={colorRowClass}>
            <ColorField
              name="designSettings.headerStartColor"
              label={t('START_COLOR') || 'Start Color'}
              fallback="#000000"
            />
            <ColorField
              name="designSettings.headerEndColor"
              label={t('END_COLOR') || 'End Color'}
              fallback="#000000"
            />
            <ColorField
              name="designSettings.titleColor"
              label={t('TITLE_COLOR') || 'Title Color'}
              fallback="#FFFFFF"
            />
          </div>
        </div>

        {/* Chat Style */}
        <div className="flex w-full flex-col gap-5 border-b border-[#e7e7e7] pb-6 dark:border-border">
          <p className={sectionTitleClass}>{t('CHAT_STYLE') || 'Chat Style'}</p>
          <div className="flex flex-col gap-[22px]">
            <div className={colorRowClass}>
              <ColorField
                name="designSettings.chatBackgroundColor"
                label={t('BACKGROUND_COLOR') || 'Background Color'}
                fallback="#FFFFFF"
              />
              <ColorField
                name="designSettings.chatFontColor"
                label={t('FONT_COLOR') || 'Font Color'}
                fallback="#000000"
              />
              <ColorField
                name="designSettings.sendButtonBackgroundColor"
                label={t('BUTTON_COLOR') || 'Button Color'}
                fallback="#000000"
              />
            </div>
            <div className={cn(colorRowClass, 'items-center')}>
              <ColorField
                name="designSettings.borderColor"
                label={t('BORDER_COLOR') || 'Border Color'}
                fallback="#e5e7eb"
              />
              <ColorField
                name="designSettings.sendButtonFontColor"
                label={t('ICON_COLOR') || 'Icon Color'}
                fallback="#FFFFFF"
              />
            </div>
          </div>
        </div>

        {/* Toggle Button */}
        <div className="flex w-full flex-col gap-5 pb-6">
          <p className={sectionTitleClass}>{t('TOGGLE_BUTTON') || 'Toggle Button'}</p>
          <div className={colorRowClass}>
            <ColorField
              name="designSettings.chat-toggle-button--background-color"
              label={t('BACKGROUND_COLOR') || 'Background Color'}
              fallback="#000000"
            />
            <ColorField
              name="designSettings.chat-toggle-button--color"
              label={t('ICON_COLOR') || 'Icon Color'}
              fallback="#FFFFFF"
            />
          </div>
        </div>
      </div>

      {canEdit ? (
        <div className="border-t border-[#e7e7e7] pt-4 dark:border-border">
          <Button
            className="h-12 w-full rounded-[62px] text-base font-semibold"
            type="submit"
            disabled={!isValid}
          >
            {t('SAVE')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
